// Komissiya qoidasi — SOF mantiq: tarmoq yo'q, tarjima yo'q, React yo'q.
//
// ⚠️ NEGA ALOHIDA FAYL VA NEGA IMPORTSIZ: qorovul (`tests/guards.mjs`
// §12) shu modulni esbuild bilan o'girib, AYNAN SHU funksiyalarni
// chaqiradi — mantiqning nusxasini emas. Bitta haqiqiy import qo'shilsa
// (zod, i18n, `money.ts`) test butun ilova daraxtini tortib kelardi va
// yurgizib bo'lmasdi. Shuning uchun bu yerda faqat `import type` bor —
// esbuild uni butunlay o'chiradi.

import type { Money } from '@/shared/lib/money'

/**
 * ⚠️ Bu ro'yxat TANIB OLISH uchun, javobni O'QISH uchun emas: sxema
 * `kind` ni satr sifatida qabul qiladi ({@link isKnownKind} dagi izoh).
 */
export const COMMISSION_KINDS = ['PERCENT', 'FIXED', 'TIER'] as const
export type CommissionKind = (typeof COMMISSION_KINDS)[number]

export interface CommissionTier {
  /** Serverda 1 dan boshlanadi; tartib SHU maydon bo'yicha */
  seq: number
  /**
   * Pog'onaning YUQORI chegarasi va u KIRITILMAYDI (`summa < upTo`).
   *
   * ⚠️ `null` — «va undan yuqorisi», faqat OXIRGI pog'onada. «10 000
   * gacha» degani aynan 10 000 KEYINGI pog'onaga tushadi (ADR-032 §2) va
   * ikkinchi talqin yo'q.
   */
  upToAmount: Money | null
  /** Bazis punkt: 250 = 2,5%. Pog'onada `percentBp` yoki `fixedAmount` — bittasi */
  percentBp: number | null
  fixedAmount: Money | null
}

export interface CommissionRule {
  id: string
  /**
   * ⚠️ YOPIQ RO'YXAT EMAS. Server to'rtinchi tur qo'shsa qat'iy enum
   * butun sahifani yiqitardi — kassir esa o'sha ekranda pul oladi. Notanish
   * turda panel «qoida bor, lekin uni o'qiy olmadim» deb ochiq aytadi.
   */
  kind: string
  /** Bazis punkt: 250 = 2,5%. `TIER` va `FIXED` da `null` */
  percentBp: number | null
  fixedAmount: Money | null
  /** ⚠️ `PERCENT`/`FIXED` da BO'SH RO'YXAT, hech qachon `null` emas */
  tiers: CommissionTier[]
  /** ISO-8601 UTC — «shu ondan boshlab kuchda» */
  validFrom: string
  /**
   * Qoida QACHON yopilgani; `null` — yopilmagan.
   *
   * ⚠️ BU MAYDON OCHIQLIK MEZONI EMAS. `non_null` sozlamasi tufayli u
   * `null` bo'lganda javobda UMUMAN bo'lmaydi, ya'ni «`validTo` yo'q»
   * degani «server aytmadi» degani ham bo'lishi mumkin. Ochiqlikni
   * {@link CommissionRule.open} aytadi; bu maydon esa BOSHQA savolga
   * kerak — yopilish sanasi KELAJAKDA bo'lsa qoida BUGUN hali kuchda.
   */
  validTo: string | null
  /**
   * Qoida hali YOPILMAGANMI (serverdagi `isOpen()`, ya'ni `validTo == null`).
   *
   * ⚠️ Server buni ALOHIDA bayroq qilib beradi va o'qiladigan yagona
   * ishonchli manba shu — yuqoridagi izohdagi sabab.
   */
  open: boolean
}

/** 1% = 100 bazis punkt */
export const BP_IN_PERCENT = 100

/**
 * Bazis punktni FOIZGA aylantiradi: 250 → 2,5.
 *
 * ⚠️ 100 GA BO'LISH TUSHIB QOLSA foiz 100 barobar katta ko'rinadi va
 * ekranda ishonarli turadi. `tests/guards.mjs` §12 bu bo'lishni AYNAN
 * shu funksiyani chaqirib qo'riqlaydi.
 */
export function percentFromBp(bp: number): number {
  return bp / BP_IN_PERCENT
}

export function isKnownKind(kind: string): kind is CommissionKind {
  return (COMMISSION_KINDS as readonly string[]).includes(kind)
}

/**
 * Qoida qachondan kuchga kirgani, millisekundda.
 *
 * ⚠️ O'qib bo'lmaydigan sana `-∞` beradi, ya'ni qoida «allaqachon
 * kuchda» deb hisoblanadi. Buzuq sanani KELAJAK deb olish qoidani
 * ekrandan butunlay yashirardi — va ekran «qoida qo'yilmagan» degan
 * yolg'onni ko'rsatardi.
 */
function startedAtMs(rule: CommissionRule): number {
  const ms = Date.parse(rule.validFrom)
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms
}

/**
 * Qoida qachon tugagani; ochiq qoidada `+∞`.
 *
 * ⚠️ USTUVORLIK `open` DA: ochiq qoidada `validTo` javobga umuman
 * yozilmaydi (`non_null`), ya'ni sanaga birinchi qarash «tugagan» degan
 * yolg'on xulosaga olib kelardi.
 */
function endedAtMs(rule: CommissionRule): number {
  if (rule.open) return Number.POSITIVE_INFINITY
  const ms = Date.parse(rule.validTo ?? '')
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms
}

/**
 * HOZIR kuchda turgan qoida.
 *
 * <h2>⚠️ ODDIYGINA «`open === true`» YETMAYDI — ikki tomondan</h2>
 *
 *   · KELAJAK: `validFrom` ertangi sana bilan qo'yilgan qoida hali
 *     ISHLAMAYDI (ADR-032 §3, «ertadan boshlab» aynan shu bilan
 *     ifodalanadi). Uni bugungi deb ko'rsatish jimgina xato bo'lardi —
 *     ikkala qoida ham `open`.
 *   · O'TMISH: yopilish sanasi KELAJAKDA bo'lgan qoida BUGUN hali
 *     kuchda va bu HAQIQIY holat, faraz emas. `CommissionServiceImpl.
 *     setRule` eskisini `closeOpenRule(..., from)` bilan YANGISINING
 *     `validFrom` ida yopadi (o'qildi 2026-08-20). Ya'ni «ertadan
 *     boshlab 3%» qo'yilgan zahoti bugungi qoida `open === false` bo'lib
 *     qoladi — uni faqat shu bayroq bo'yicha saralash bugungi foizni
 *     ekrandan yo'qotib, «qoida qo'yilmagan» degan YOLG'ON ogohlantirish
 *     chiqarardi.
 *
 * Shart shuning uchun oraliq va u serverdagining AYNAN o'zi
 * (`CommissionRuleRepository#findEffective`):
 * `valid_from <= at AND (valid_to IS NULL OR valid_to > at)`, tanlov esa
 * `ORDER BY valid_from DESC`.
 */
export function activeRule(
  rules: readonly CommissionRule[],
  nowMs: number,
): CommissionRule | null {
  let best: CommissionRule | null = null
  for (const rule of rules) {
    if (startedAtMs(rule) > nowMs || endedAtMs(rule) <= nowMs) continue
    if (best === null || startedAtMs(rule) >= startedAtMs(best)) best = rule
  }
  return best
}

/**
 * KELAJAKDA kuchga kiradigan eng yaqin qoida; yo'q bo'lsa `null`.
 *
 * Kassir foiz o'zgarishini oldindan bilishi kerak: aks holda o'zgargan
 * kuni u eski raqamni aytib, boshqasini oladi.
 */
export function upcomingRule(
  rules: readonly CommissionRule[],
  nowMs: number,
): CommissionRule | null {
  let soonest: CommissionRule | null = null
  for (const rule of rules) {
    if (startedAtMs(rule) <= nowMs || endedAtMs(rule) <= nowMs) continue
    if (soonest === null || startedAtMs(rule) < startedAtMs(soonest)) soonest = rule
  }
  return soonest
}

export interface TierBounds {
  /** Pastki chegara — KIRITILADI. `null` = noldan */
  from: Money | null
  /** Yuqori chegara — KIRITILMAYDI. `null` = «va undan yuqorisi» */
  to: Money | null
}

/**
 * Pog'onaning chegaralari.
 *
 * ⚠️ Pastki chegara ALOHIDA maydon EMAS — u oldingi pog'onaning yuqori
 * chegarasi. Server uni bermaydi va bermasligi to'g'ri: ikkita mustaqil
 * son bo'lsa, ular bir-biriga mos kelmay qolishi mumkin edi.
 *
 * ⚠️ Ro'yxat `seq` bo'yicha TARTIBLANGAN bo'lishi shart
 * (`toCommissionRule` shuni ta'minlaydi): tartibsiz ro'yxatda bu funksiya
 * «10 000 gacha 2» ni «10 000 gacha 5» ga aylantirardi.
 */
export function tierBounds(tiers: readonly CommissionTier[], index: number): TierBounds {
  return {
    from: index > 0 ? (tiers[index - 1]?.upToAmount ?? null) : null,
    to: tiers[index]?.upToAmount ?? null,
  }
}
